import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { OrderService } from 'src/app/order.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from 'src/app/models/store';
import { CardDetails } from 'src/app/models/order';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { PaymentMethodsBottomSheetComponent } from 'src/app/order-summary/payment-methods-bottom-sheet/payment-methods-bottom-sheet-component';
import { PaymentOptionService, PaymentOption } from 'src/app/_services/payment-option.service';
import { Constants } from 'src/app/constants';
import {WalletComponent} from '../../order-summary/wallet/wallet.component';
import {MatDialog} from '@angular/material/dialog';
import { Subject, Subscription, interval } from 'rxjs';
import { takeWhile } from 'rxjs/operators';

@Component({
  selector: 'app-offer-summary',
  templateUrl: './offer-summary.component.html',
  styleUrls: ['./offer-summary.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OfferSummaryComponent implements OnInit, OnDestroy {
  private paymentOptionSub: Subscription;
  storeId;
  offerOrderId;
  offersInCart;
  store:Store;
  formQuantity = Array.from(Array(1).keys()).map(n => n+1);
  displayedColumns: string[] = ['quantity', 'item', 'itemTotal'];
  isLoading = true;
  phoneNumber
  otpSent
  isOtpSending: boolean = false;
  otpSending = false;
  otp
  preferredCard
  guestCardDetails:CardDetails[];
  paymentGateway;
  paymentOption:PaymentOption;
  isSubmitting = false;  // ENG-4505: prevent double-submit on gift card purchase
  isPaymentProcessing = false;  // ENG-5090: payment in progress (polling state)
  private pollingActive = false;
  giftDetails: any = null;  // ENG-5034: gift recipient details captured on the offer page

  constructor(
    private route: ActivatedRoute,
    private bottomSheet: MatBottomSheet,
    private router: Router,
    private orderService:OrderService,
    private paymentOptionService:PaymentOptionService,
    private ref: ChangeDetectorRef,
    private dialog: MatDialog) { }

  ngOnInit() {
    this.storeId = this.route.snapshot.paramMap.get('storeId');
    this.offerOrderId = this.route.snapshot.paramMap.get('offerOrderId');

    this.paymentOptionSub = this.paymentOptionService.current.subscribe(paymentOption =>{
      if(paymentOption){
        this.paymentOption = paymentOption;
        this.loadOffer();
        this.ref.markForCheck();
      }
    })

    this.loadOffer();
  }

  private loadGiftDetails() {
    const saved = localStorage.getItem(`giftDetails_${this.offerOrderId}`);
    if (saved) {
      try {
        this.giftDetails = JSON.parse(saved);
      } catch (e) {
        this.giftDetails = null;
      }
    } else {
      this.giftDetails = null;
    }
  }

  removeGift() {
    localStorage.removeItem(`giftDetails_${this.offerOrderId}`);
    this.giftDetails = null;
    this.ref.markForCheck();
  }

  editGift() {
    const firstItem = this.offersInCart?.selected_offers?.[0];
    if (firstItem) {
      this.router.navigate(['store', this.storeId, 'offers', 'order', this.offerOrderId, 'offer', firstItem.offer.id]);
    }
  }

  private loadOffer() {
    this.isLoading = true;
    this.loadGiftDetails();
    this.orderService.getOffer(this.storeId, this.offerOrderId)
    .subscribe(offersInCart => {
      if(offersInCart.offer_order.is_submitted){
        // ENG-5090: if payment is still in progress, poll instead of navigating to success
        if (offersInCart.offer_order.payment_status === 'IN_PROGRESS') {
          this.isPaymentProcessing = true;
          this.startPaymentPolling();
        } else {
          this.router.navigate(['store', this.storeId,'offer', 'order', this.offerOrderId, 'status', 'success']);
        }
      }
      this.offersInCart = offersInCart;
      this.paymentGateway = offersInCart.store.payment_gateway;

      if(this.offersInCart.offer_order.phone_number){
        this.phoneNumber = this.getFormattedPhoneNumber(this.offersInCart.offer_order.phone_number);
      }
      this.store = Object.assign(new Store(), offersInCart.store);
      if(this.offersInCart.selected_offers.length <= 0){
        this.router.navigate(['store', this.storeId, 'offers', 'order', this.offerOrderId, 'available'])
      }
      this.orderService.getPreferredPaymentForOffer(this.getStoreId(), this.offerOrderId, this.getPlainPhoneNumber(), this.paymentGateway).subscribe(data => {
        this.preferredCard = Object.assign(new CardDetails(), data['guest_payment_detail'])
        this.isLoading = false;
        this.ref.markForCheck();
      },
      error => {
        this.isLoading = false;
        console.log(error)
        this.ref.markForCheck();
      });
      this.ref.markForCheck();
    })
  }

  getPlainPhoneNumber(){
    if(this.phoneNumber){
      let ph = this.phoneNumber.replace(/[\s()-]/g, '')
      return '+1' + ph.substring(0,10)
    }
    return "";
  }

  getPlainPhoneNumberFromString(phoneNumber:string){
    let ph = phoneNumber.replace(/[\s()-]/g, '')
    return '+1' + ph.substring(0,10)
  }

  getFormattedPhoneNumber(phoneNumber){
    return phoneNumber.replace(/^(\+1)(\d{0,3})(\d{0,3})(\d{0,4})/, '($2) $3-$4')
  }

  updateQuantity(item){
    if(item.quantity <= 0){
      this.deleteItem(item);
    }else{
      this.orderService.updateOfferQuantity(this.storeId, this.offerOrderId, item.id, item.quantity).subscribe(data => {
        this.loadOffer();
      })
    }
  }

  deleteItem(item){
    this.orderService.deleteOffer(this.storeId, this.offerOrderId, item.id).subscribe(data =>{
      this.loadOffer();
    })
  }

  onSelect(item){
    // parsed_item_id = item.id
    this.router.navigate(['store', this.storeId, 'offers', 'order', this.offerOrderId, 'offer', item.offer.id]);
  }

  goBack(){
    this.router.navigate(['store', this.storeId, 'offers', 'order', this.offerOrderId, 'available']);
  }

  sendOTP(){
    if(this.otpSending) return;
    this.otpSending = true;
    this.orderService.otpForOffer(this.storeId, this.offerOrderId, this.getPlainPhoneNumber()).subscribe(() => {
      this.otpSent = true;
      this.otpSending = false;
      this.ref.markForCheck();
    }, () => {
      this.otpSending = false;
      this.ref.markForCheck();
    })
  }

  verifyOTP(){
    this.orderService.verifyOtpForOffer(this.storeId, this.offerOrderId, this.getPlainPhoneNumber(), this.otp).subscribe(data => {
      localStorage.setItem(Constants.PHONE_NUMBER_KEY, this.getPlainPhoneNumber())
      this.offersInCart.offer_order.phone_number_verified = data['is_verified']
      this.orderService.getLastPreferredPaymentForOffer(this.storeId, this.offerOrderId, this.getPlainPhoneNumber()).subscribe(data => {
        if(!data['is_cash']){
          this.preferredCard = Object.assign(new CardDetails(), data['guest_payment_detail'])
        }
        this.ref.markForCheck();
      }, () => {
        this.preferredCard = null;
        this.ref.markForCheck();
      })
      this.ref.markForCheck();
    })
  }

  changePhone(){
    this.offersInCart.offer_order.phone_number_verified = false;
    this.otpSent = false;
    this.isOtpSending = false;
    this.otpSending = false;
  }

  updatePhoneNumber(phoneNumber:string){
    if(this.getPlainPhoneNumberFromString(phoneNumber).length === 12 && !this.otpSent && !this.otpSending){
      this.sendOTP()
    }
  }

  changeSelectedCard(){
    this.isLoading = true;
    this.orderService.getCards(this.store.id,
      this.getPlainPhoneNumber(), this.paymentGateway).subscribe(data => {
      this.guestCardDetails = data['cards'].map(o => Object.assign(new CardDetails(), o));
      this.isLoading = false;
    },
    (error) => {
      this.guestCardDetails = []
      this.isLoading = false;
    }).add(()=>{
      console.log(this.guestCardDetails);

      this.bottomSheet.open(PaymentMethodsBottomSheetComponent, {
        panelClass: 'payment-bottom-sheet-container',
        data:  {
          isOffer: true,
          orderHash: this.offerOrderId,
          phoneNumber: this.getPlainPhoneNumber(),
          orderTotal: this.offersInCart.payment.total,
          storeCredit: null,
          cardDetails: this.guestCardDetails,
          paymentGateway: this.paymentGateway,
          storeId: this.store.id,
          storeCreditEnabled: false,
          storeCashEnabled: false,
          storeCardEnabled: this.store.has_card_payment,
          surcharge: this.store.surcharge_percent,
          payrocUsername: this.store.payroc_username,
          store: this.store
        }
      }).afterDismissed().subscribe(paymentMethod => {
        console.log(paymentMethod);
        if(paymentMethod){
          this.preferredCard = paymentMethod.cardDetails
          this.paymentOptionService.updatePaymentOption(paymentMethod)
          this.loadOffer()
        }
      });
    });
  }

  getStoreId(){
    return this.offersInCart.store.id;
  }

  getPaymentGateway(){
    console.log(this.offersInCart.store.payment_gateway);
  }

  // checkLastPreferredPayment(){
  //   if(this.getPlainPhoneNumber().length === 12){
  //     this.orderService.getPreferredPaymentForOffer(this.storeId,
  //       this.offerOrderId,
  //       this.getPlainPhoneNumber(),
  //       this.paymentGateway).subscribe(data => {
  //       if(!data['is_cash']){
  //         this.preferredCard = Object.assign(new CardDetails(), data['guest_payment_detail'])
  //       }
  //     }, () => {
  //       this.preferredCard = null;
  //     })
  //   }
  // }

  deleteCard(card:CardDetails){
    this.orderService.deleteCard(card).subscribe(() => {
      let index = this.guestCardDetails.indexOf(card, 1)
      if(index > -1){
        this.guestCardDetails.splice(index, 1)
      }
      // this.checkLastPreferredPayment()
    })
  }

  claimNow(){
    // ENG-4505: prevent double-submit — ignore if already in-flight
    if (this.isSubmitting || this.isPaymentProcessing) {
      return;
    }
    this.isSubmitting = true;

    this.orderService.submitOfferOrder(this.getStoreId(), this.offerOrderId).subscribe(
      (response) => {
        if (response.status === 202) {
          // ENG-5090: payment still processing, start polling
          this.isSubmitting = false;
          this.isPaymentProcessing = true;
          this.ref.markForCheck();
          this.startPaymentPolling();
        } else {
          this.router.navigate(['store', this.storeId,'offer', 'order', this.offerOrderId, 'status', 'success']);
        }
      },
      () => {
        this.isSubmitting = false;  // allow retry on failure
        this.router.navigate(['store', this.storeId,'offer', 'order', this.offerOrderId, 'status', 'failure']);
      })
  }

  private startPaymentPolling() {
    this.pollingActive = true;
    interval(5000).pipe(
      takeWhile(() => this.pollingActive)
    ).subscribe(() => {
      this.orderService.checkOfferPaymentStatus(this.getStoreId(), this.offerOrderId).subscribe(
        (result) => {
          if (result.status === 'completed') {
            this.pollingActive = false;
            this.router.navigate(['store', this.storeId, 'offer', 'order', this.offerOrderId, 'status', 'success']);
          } else if (result.status === 'failed') {
            this.pollingActive = false;
            this.isPaymentProcessing = false;
            this.ref.markForCheck();
            this.router.navigate(['store', this.storeId, 'offer', 'order', this.offerOrderId, 'status', 'failure']);
          }
          // 'in_progress' — keep polling
        },
        () => {
          // polling error — keep trying
        }
      );
    });
  }

  ngOnDestroy() {
    this.pollingActive = false;
    if (this.paymentOptionSub) {
      this.paymentOptionSub.unsubscribe();
    }
  }

  openWallet(){
    console.log("wallet")
    let dialogRef = this.dialog.open(WalletComponent, {
      disableClose: true,
      data:  {order: this.offersInCart, selectMode: true, forOffer: true},
      panelClass: ['full-screen-modal']
    });
    dialogRef.afterClosed().subscribe(data => {
      console.log(data);
      this.paymentOption = data;
      this.orderService.updatePreferredPaymentAsCardForOffer(this.getStoreId(), this.offerOrderId,
        data.cardDetail.id).subscribe(data => {
        this.paymentOptionService.updatePaymentOption(this.paymentOption)
          console.log(data);
      })
    })
  }
}
